import { z } from 'zod';
import { projectTypeEnum, projectStatusEnum } from './project';

// ==================== CLIENT ====================
export const clientSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  name: z.string().min(1).max(255),
  company: z.string().max(255).nullable().optional(),
  email: z.string().email().nullable().optional(),
  phone: z.string().max(50).nullable().optional(),
  notes: z.string().max(1000).nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type Client = z.infer<typeof clientSchema>;

export const createClientRequestSchema = z.object({
  name: z.string().min(1).max(255),
  company: z.string().max(255).optional(),
  email: z.string().email().optional(),
  phone: z.string().max(50).optional(),
  notes: z.string().max(1000).optional(),
});

export type CreateClientRequest = z.infer<typeof createClientRequestSchema>;

export const updateClientRequestSchema = z.object({
  name: z.string().min(1).max(255).optional(),
  company: z.string().max(255).nullable().optional(),
  email: z.string().email().nullable().optional(),
  phone: z.string().max(50).nullable().optional(),
  notes: z.string().max(1000).nullable().optional(),
});

export type UpdateClientRequest = z.infer<typeof updateClientRequestSchema>;

// ==================== CLIENT PORTAL ====================
export const clientPortalSchema = z.object({
  id: z.string().uuid(),
  clientId: z.string().uuid(),
  token: z.string(),
  hasPassword: z.boolean(),
  isActive: z.boolean(),
  brandingConfig: z.object({
    logo: z.string().optional(),
    primaryColor: z.string().optional(),
    companyName: z.string().optional(),
  }).nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type ClientPortal = z.infer<typeof clientPortalSchema>;

export const updatePortalRequestSchema = z.object({
  password: z.string().min(4).max(100).optional(),
  removePassword: z.boolean().optional(),
  isActive: z.boolean().optional(),
  brandingConfig: z.object({
    logo: z.string().optional(),
    primaryColor: z.string().optional(),
    companyName: z.string().optional(),
  }).optional(),
});

export type UpdatePortalRequest = z.infer<typeof updatePortalRequestSchema>;

// ==================== PORTAL FILES ====================
export const portalFileSchema = z.object({
  id: z.string().uuid(),
  portalId: z.string().uuid(),
  fileId: z.string().uuid(),
  displayName: z.string().nullable().optional(),
  displayOrder: z.number().int(),
  addedAt: z.string().datetime(),
  // Joined file data
  file: z.object({
    id: z.string().uuid(),
    name: z.string(),
    mimeType: z.string(),
    size: z.number(),
    updatedAt: z.string().datetime(),
  }).optional(),
});

export type PortalFile = z.infer<typeof portalFileSchema>;

export const addPortalFilesRequestSchema = z.object({
  fileIds: z.array(z.string().uuid()).min(1).max(50),
});

export type AddPortalFilesRequest = z.infer<typeof addPortalFilesRequestSchema>;

export const reorderPortalFilesRequestSchema = z.object({
  fileOrders: z.array(z.object({
    portalFileId: z.string().uuid(),
    order: z.number().int().min(0),
  })),
});

export type ReorderPortalFilesRequest = z.infer<typeof reorderPortalFilesRequestSchema>;

// ==================== PORTAL ACTIVITY ====================
export const portalActivitySchema = z.object({
  id: z.string().uuid(),
  portalId: z.string().uuid(),
  eventType: z.enum(['view', 'download', 'access']),
  fileId: z.string().uuid().nullable().optional(),
  fileName: z.string().optional(), // Joined from file
  ipAddress: z.string().nullable().optional(),
  userAgent: z.string().nullable().optional(),
  createdAt: z.string().datetime(),
});

export type PortalActivity = z.infer<typeof portalActivitySchema>;

// ==================== PUBLIC PORTAL (for clients) ====================
export const publicPortalInfoSchema = z.object({
  companyName: z.string().optional(),
  clientName: z.string(),
  hasPassword: z.boolean(),
  brandingConfig: z.object({
    logo: z.string().optional(),
    primaryColor: z.string().optional(),
    companyName: z.string().optional(),
  }).nullable().optional(),
});

export type PublicPortalInfo = z.infer<typeof publicPortalInfoSchema>;

export const publicPortalFileSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  displayName: z.string().nullable().optional(),
  mimeType: z.string(),
  size: z.number(),
  updatedAt: z.string().datetime(),
});

export type PublicPortalFile = z.infer<typeof publicPortalFileSchema>;

export const verifyPortalPasswordRequestSchema = z.object({
  password: z.string().min(1),
});

export type VerifyPortalPasswordRequest = z.infer<typeof verifyPortalPasswordRequestSchema>;

// ==================== CLIENT WITH PORTAL (for list view) ====================
export const clientWithPortalSchema = clientSchema.extend({
  portal: clientPortalSchema.nullable().optional(),
  fileCount: z.number().int().optional(),
  lastActivity: z.string().datetime().nullable().optional(),
});

export type ClientWithPortal = z.infer<typeof clientWithPortalSchema>;

// ==================== PUBLIC PORTAL PROJECTS (for client portal) ====================
export const publicPortalProjectSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  type: projectTypeEnum,
  status: projectStatusEnum,
  folderCount: z.number().int(),
  fileCount: z.number().int(),
});

export type PublicPortalProject = z.infer<typeof publicPortalProjectSchema>;

export const publicPortalFolderSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  parentId: z.string().uuid().nullable(),
});

export type PublicPortalFolder = z.infer<typeof publicPortalFolderSchema>;
